import { useMemo, useState } from "react"
import { getHistoryForLoop, getRecordedLoops, type HistoryEntry } from "../game/historyLog"
import { formatDay } from "../game/calendarEngine"

interface HistoryScreenProps {
  /** The loop the player is living now, shown first and by default. */
  currentLoop: number
  onBack: () => void
}

const KIND_LABEL: Record<HistoryEntry["kind"], string> = {
  world: "Event",
  gain: "Gained",
  battle: "Battle",
  loss: "Loss",
  milestone: "Milestone",
}

/**
 * The chronicle of a loop, in the order it happened (see historyLog). The
 * digest that pops up between actions is fleeting; this is where the player
 * comes to read back what the world did while they were looking elsewhere,
 * including in loops now behind them.
 */
export function HistoryScreen({ currentLoop, onBack }: HistoryScreenProps) {
  const recordedLoops = useMemo(() => {
    const loops = getRecordedLoops()
    // The current loop is always offered, even before it has any entries.
    return loops.includes(currentLoop) ? loops : [currentLoop, ...loops]
  }, [currentLoop])

  const [selectedLoop, setSelectedLoop] = useState(currentLoop)
  const entries = useMemo(() => getHistoryForLoop(selectedLoop), [selectedLoop])

  return (
    <div className="screen history-screen">
      <h1>Chronicle</h1>
      <p className="journal-intro">
        Everything that happened, loop by loop — the reports the world sent while
        you were elsewhere, and the marks you left on it.
      </p>

      {recordedLoops.length > 1 && (
        <div className="history-loop-picker" role="group" aria-label="Loop">
          {recordedLoops.map((loop) => (
            <button
              key={loop}
              type="button"
              className={loop === selectedLoop ? "selected" : ""}
              aria-pressed={loop === selectedLoop}
              onClick={() => setSelectedLoop(loop)}
            >
              Loop {loop}
              {loop === currentLoop ? " (now)" : ""}
            </button>
          ))}
        </div>
      )}

      {entries.length === 0 ? (
        <p>
          Nothing recorded for Loop {selectedLoop} yet. What happens as the days
          pass will be set down here.
        </p>
      ) : (
        <ol className="history-list">
          {entries.map((entry, index) => (
            <li key={index} className={`history-entry history-${entry.kind}`}>
              <span className="history-day">{formatDay(entry.day)}</span>
              <span className={`history-kind history-kind-${entry.kind}`}>
                {KIND_LABEL[entry.kind]}
              </span>
              <span className="history-text">{entry.text}</span>
            </li>
          ))}
        </ol>
      )}

      <button type="button" onClick={onBack}>
        Back to Map
      </button>
    </div>
  )
}
